import { Component } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { NzTreeFlatDataSource, NzTreeFlattener } from 'ng-zorro-antd/tree-view';
import { CommonService } from '../../../../shared/services/common.service';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

export interface NzTreeNode {
  level: number;
  title: string;
  key: string;
  children?: NzTreeNode[];
  isLeaf?: boolean;
  expanded?: boolean;
  extraData?: any;
  formData?: any,
  ancestors?: any[];
  type: 'location' | 'group';
  parent?: any;
  expandable: boolean; // Add this property
}
export interface FlatNode {
  title: string;
  key: string;
  isLeaf?: boolean;
  children?: FlatNode[];
  expandable: boolean;
  level: number;
}
export interface SpocDetail {
  name: string;
  email: string;
  contact: string;
  designation: string;
}

export interface Location {
  location_id?: number;
  registered_name: string;
  ancestors: string[];
  spoc_detail: SpocDetail[];
  display_name: string;
  registered_address: string | null;
  latitude?: number | null;
  longitude?: number | null;
  customer_id: number;
  is_deleted: boolean;
}

@Component({
  selector: 'app-group-management',
  templateUrl: './group-management.component.html',
  styleUrls: ['./group-management.component.scss'],
})
export class GroupManagementComponent {
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  private strictEmailValidator = (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? '';
    if (!value) return null; // handled by Validators.required
    return this.emailPattern.test(value) ? null : { strictEmail: true };
  };
  selectedNode: FlatNode | null = null;
  isGroupModal: boolean = false;
  isLocationModal: boolean = false;
  isMoveModal: boolean = false;
  moveType: 'location' | 'group' = 'location';
  selectedTargetGroupId: number | null = null;
  availableGroups: { id: number | null; name: string; level: number }[] = [];
  modalType!: 'add' | 'edit' | 'view';
  isNoChildren: boolean = false;
  isSelectionPopup: boolean = false;
  isGroupDirty: boolean = false;
  newLocationList: any[] = [];
  updateList: any[] = []; // Single list for both location and group updates
  groupForm: FormGroup = new FormGroup({
    group_name: new FormControl('', [Validators.required]),
  });
  // Store original data for comparison
  private originalLocationData: any = null;
  private originalGroupData: any = null;
  locationForm: FormGroup = new FormGroup({
    registered_name: new FormControl('', [Validators.required]),
    display_name: new FormControl('', [Validators.required]),
    registered_address: new FormControl('', [Validators.required]),
    latitude: new FormControl<number | null>(null),
    longitude: new FormControl<number | null>(null),
    spoc_detail: new FormArray([]),
  });
  treeData: NzTreeNode[] = [];
  flatNodeMap = new Map<FlatNode, NzTreeNode>();
  nestedNodeMap = new Map<NzTreeNode, FlatNode>();
  selectListSelection = new SelectionModel<FlatNode>(false);
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  customer_id: string = '';
  get spocDetail() {
    return this.locationForm.get('spoc_detail') as FormArray;
  }
  private populateFlatNodeMap(nodes: NzTreeNode[]): void {
    nodes.forEach(node => {
      const flatNode = this.transformer(node, node.level || 0);
      this.flatNodeMap.set(flatNode, node);
      this.nestedNodeMap.set(node, flatNode);
      if (node.children) {
        this.populateFlatNodeMap(node.children);
      }
    });
  }
  addNewSpoc(): void {
    const spocGroup = this.fb.group({
      designation: ['', Validators.required],
      name: ['', Validators.required],
      email: ['', [Validators.required, this.strictEmailValidator]],
      contact: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    });
    this.spocDetail.push(spocGroup);
  }
  removeSpocDetail(index: number): void {
    this.spocDetail.removeAt(index);
  }
  deleteLocationSpoc(i: number) {
    this.spocDetail.removeAt(i);
  }

  onAddressChange(event: { location?: { lat?: number | null; lng?: number | null }; formattedAddress?: string | null }): void {
    this.locationForm.patchValue({
      latitude: event?.location?.lat ?? null,
      longitude: event?.location?.lng ?? null,
      registered_address: event?.formattedAddress ?? '',
    }, { emitEvent: false });
  }
  private transformer = (node: NzTreeNode, level: number): FlatNode => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.key === node.key ? existingNode : {
        expandable: node.type === 'group' ? true : false, // Groups are always expandable, locations are not
        title: node.title,
        level,
        key: node.key,
      };
    flatNode.key = node.key;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };
  treeFlattener = new NzTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  dataSource = new NzTreeFlatDataSource(this.treeControl, this.treeFlattener);
  hasChild = (_: number, node: FlatNode): boolean => node.expandable;
  hasNoContent = (_: number, node: FlatNode): boolean => node.title === '';
  trackBy = (_: number, node: FlatNode): string => `${node.key}-${node.title}`;

  getLocationId(node: FlatNode): number | string | null {
    if (!node || node.expandable) return null; // only locations (leaf nodes)
    const treeNode = this.flatNodeMap.get(node) || this.findTreeNodeByKey(node.key);
    const id = treeNode?.extraData?.location_id ?? null;
    return id == null ? null : id;
  }
  constructor(
    private commonSvc: CommonService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
    this.getLocationTree();
  }

  getLocationTree() {
    this.commonSvc.getLocation(Number(this.customer_id)).subscribe((res: any) => {
      if (res?.status === 200) {
        this.treeData = this.commonSvc.convertToNzTreeSelectFormat(res?.data);
        this.dataSource.setData(this.treeData);
        this.treeControl.expandAll();
        this.populateFlatNodeMap(this.treeData);
      }
    });
  }
  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if (this.isGroupDirty) {
      return new Observable<boolean>((observer) => {
        this.commonSvc.modalSvc.warning({
          nzTitle: 'Unsaved Changes',
          nzContent:
            "You have unsaved changes that won't be saved if you change route. Are you sure you want to leave?",
          nzOkText: 'Yes',
          nzCancelText: 'No',
          nzOnOk: () => {
            observer.next(true);
            observer.complete();
          },
          nzOnCancel: () => {
            observer.next(false);
            observer.complete();
          },
        });
      });
    }
    return true;
  }

  // Helper to find NzTreeNode by key
  private findTreeNodeByKey(key: string, nodes: NzTreeNode[] = this.treeData): NzTreeNode | null {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children && node.children.length > 0) {
        const found = this.findTreeNodeByKey(key, node.children);
        if (found) return found;
      }
    }
    return null;
  }

  // Helper to check if a group has locations (prevents further nesting)
  private groupHasLocations(node: NzTreeNode): boolean {
    if (!node.children) return false;
    return node.children.some(child => child.type === 'location');
  }

  // Public method for template to check if a group has locations
  public hasLocations(node: FlatNode): boolean {
    const treeNode = this.flatNodeMap.get(node) || this.findTreeNodeByKey(node.key);
    return treeNode ? this.groupHasLocations(treeNode) : false;
  }

  nodeClick(node: FlatNode, type: 'add' | 'edit' | 'view'): any {
    this.selectedNode = node;
    this.modalType = type;
    this.groupForm.reset();
    this.locationForm.reset();
    this.spocDetail.clear();
    // Try flatNodeMap first, fallback to searching by key
    let data = this.flatNodeMap.get(node) || this.findTreeNodeByKey(node.key);

    if (type === 'add') {
      // Check if this group already has locations (prevents further nesting)
      if (data && this.groupHasLocations(data)) {
        this.commonSvc.message.warning('Cannot add groups to a group that already contains locations. You can only add more locations.');
        // Directly open location modal instead of selection popup
        this.addLocationHandler();
        return;
      }
      // Show selection popup to choose between adding group or location
      this.isSelectionPopup = true;
    }

    if ((type === 'edit' || type === 'view') && !node.expandable) {
      this.isLocationModal = true;
      this.spocDetail.clear();
      this.addNewSpoc();
      this.locationForm.patchValue(data?.extraData);
      // Store original data for comparison during edit
      if (type === 'edit') {
        this.originalLocationData = JSON.parse(JSON.stringify(data?.extraData));
      }
      // Handle both spoc_detail and spoc_details (API might use either)
      const spocDetailData = data?.extraData?.spoc_detail || data?.extraData?.spoc_details || [];
      const spocDetailFormArray = this.spocDetail.controls;
      spocDetailFormArray.splice(0, spocDetailFormArray.length);
      spocDetailData.forEach((spoc: any) => {
        spocDetailFormArray.push(
          this.fb.group({
            designation: [spoc.designation, Validators.required],
            name: [spoc.name, Validators.required],
            email: [spoc.email, [Validators.required, this.strictEmailValidator]],
            contact: [spoc.phone ?? spoc.contact ?? '', [Validators.required, Validators.pattern(/^\d{10}$/)]],
          })
        ); 
      });
      if (type === 'edit') this.locationForm.enable({ onlySelf: true })
      if (type === 'view') this.locationForm.disable({ onlySelf: true })
    }
    if (type === 'edit' && node.expandable) {
      this.isGroupModal = true;
      this.groupForm.get('group_name')?.setValue(data?.title);
      // Store original group data for comparison
      this.originalGroupData = {
        group_id: data?.extraData?.group_id,
        group_name: data?.title,
        parent_group_id: data?.parent?.extraData?.group_id || null
      };
    }
  }
  addLocationHandler() {
    this.locationForm.reset();
    this.spocDetail.clear();
    this.isSelectionPopup = false;
    this.isLocationModal = true;
    this.addNewSpoc();
  }
  addGroupHandler() {
    // Check if selected group already has locations (prevents further nesting)
    if (this.selectedNode) {
      const data = this.flatNodeMap.get(this.selectedNode) || this.findTreeNodeByKey(this.selectedNode.key);
      if (data && this.groupHasLocations(data)) {
        this.commonSvc.message.warning('Cannot add groups to a group that already contains locations. You can only add more locations.');
        return;
      }
    }

    this.isSelectionPopup = false;
    this.isGroupModal = true;
    this.modalType = 'add'; // Set modal type for consistency
  }
  private createDemoLocationNode(parentLevel: number): NzTreeNode {
    return {
      key: 'temp-demo-node',
      title: 'demo',
      children: [],
      extraData: {},
      isLeaf: true,
      level: parentLevel + 1,
      type: 'location',
      expandable: false
    };
  }
  private createGroupNode(title: string, parentNode: NzTreeNode): NzTreeNode {
    return {
      key: `group-${Math.random().toString(36).substring(2, 10)}`,
      title,
      children: [], // Start with empty children
      extraData: { group_name: title }, // New group - no group_id yet
      isLeaf: false,
      level: parentNode.level + 1,
      type: 'group',
      parent: parentNode,
      expandable: true
    };
  }
  protected saveGroup() {
    if (!this.commonSvc.validate(this.groupForm)) {
      return;
    }
    const groupName = this.groupForm.value.group_name?.trim();
    if (!groupName) {
      this.commonSvc.message.error('Group name is required');
      return;
    }

    // Handle case when no node is selected (creating root group)
    if (!this.selectedNode) {
      // Create a root level group (UI only, no API call needed)
      const rootNode: NzTreeNode = {
        key: `root-${Math.random().toString(36).substring(2, 10)}`,
        title: groupName,
        children: [], // Start with empty children
        extraData: { group_name: groupName }, // New group - no group_id yet
        isLeaf: false,
        level: 0,
        type: 'group',
        expandable: true
      };

      this.treeData.push(rootNode);
      const flatNode = this.transformer(rootNode, 0);
      this.flatNodeMap.set(flatNode, rootNode);
      this.nestedNodeMap.set(rootNode, flatNode);
      this.dataSource.setData(this.treeData);
      // Expand the newly created group so it's ready for adding children
      this.treeControl.expand(flatNode);
      this.groupForm.reset();
      this.isGroupModal = false;
      return;
    }

    const parentNode = this.flatNodeMap.get(this.selectedNode);
    if (!parentNode) {
      // If no parent node, create a root level group
      const rootNode: NzTreeNode = {
        key: `root-${Math.random().toString(36).substring(2, 10)}`,
        title: groupName,
        children: [], // Start with empty children instead of demo node
        extraData: { group_name: groupName }, // New group - no group_id yet
        isLeaf: false,
        level: 0,
        type: 'group',
        expandable: true
      };
      this.treeData.push(rootNode);
      const flatNode = this.transformer(rootNode, 0);
      this.flatNodeMap.set(flatNode, rootNode);
      this.nestedNodeMap.set(rootNode, flatNode);
      this.dataSource.setData(this.treeData);
      // Expand the newly created group so it's ready for adding children
      this.treeControl.expand(flatNode);
      this.groupForm.reset();
      this.isGroupModal = false;
      return;
    }
    try {
      if (this.modalType === 'add') {
        this.handleAddGroup(groupName, parentNode);
      } else if (this.modalType === 'edit') {
        this.handleEditGroup(groupName, parentNode);
      }
      this.groupForm.reset();
      this.isGroupModal = false;
    } catch (error) {
      this.commonSvc.message.error('Failed to save group');
      console.error('Error saving group:', error);
    }
  }
  private handleAddGroup(groupName: string, parentNode: NzTreeNode): void {
    if (this.checkDuplicate(this.treeData, groupName)) {
      this.commonSvc.message.error('Group name already exists!');
      return;
    }
    const newGroupNode = this.createGroupNode(groupName, parentNode);
    if (!parentNode.children) {
      parentNode.children = [];
    }
    parentNode.children.push(newGroupNode);
    const flatNode = this.transformer(newGroupNode, parentNode.level + 1);
    this.flatNodeMap.set(flatNode, newGroupNode);
    this.nestedNodeMap.set(newGroupNode, flatNode);
    this.dataSource.setData(this.treeData);
    // Expand the parent node to show the newly created group
    if (this.selectedNode) {
      this.treeControl.expand(this.selectedNode);
    }
    // Also expand the newly created group so it's ready for adding children
    this.treeControl.expand(flatNode);
  }
  private handleEditGroup(groupName: string, parentNode: NzTreeNode): void {
    parentNode.title = groupName;
    if (!parentNode.children) {
      parentNode.children = [];
    }
    this.updateTreeData(this.treeData, parentNode.key, parentNode);
    this.dataSource.setData([...this.treeData]);
    if (this.selectedNode) {
      this.treeControl.expand(this.selectedNode);
    }

    // Check if group name was actually changed
    const nameChanged = this.originalGroupData?.group_name !== groupName;

    if (nameChanged) {
      const groupId = parentNode.extraData?.group_id;

      if (!groupId) {
        this.commonSvc.message.error('Cannot update group: missing group ID');
        return;
      }

      const updatePayload = {
        type: 'group',
        group_id: groupId,
        updateData: { group_name: groupName }
      };

      const existingIndex = this.updateList.findIndex(
        (item: any) => item.type === 'group' && item.group_id === groupId
      );
      if (existingIndex > -1) {
        this.updateList[existingIndex].updateData.group_name = groupName;
      } else {
        this.updateList.push(updatePayload);
      }

      // Mark as dirty when group name changes (enables submit button)
      this.isGroupDirty = true;
    }
  }
  protected saveLocation() {
    if (this.commonSvc.validate(this.locationForm)) {
      if (this.modalType === 'add') {
        if (!this.selectedNode) return;
        const parentNode = this.flatNodeMap.get(this.selectedNode);
        if (!parentNode) return;
        if (this.checkDuplicate(this.treeData, this.locationForm.value.display_name)) {
          this.commonSvc.message.error('Location or group with the same name already exists!');
          return;
        }
        const newLocationNode: NzTreeNode = {
          title: this.locationForm.value.display_name,
          key: Math.random().toString(36).substring(2, 10) + (new Date()).getTime().toString(36),
          isLeaf: true,
          level: parentNode.level + 1,
          formData: this.locationForm.value,
          type: 'location',
          extraData: { ...this.locationForm.value },
          parent: parentNode,
          expandable: false,
        };
        this.makePayloadData(newLocationNode, 'add');
        if (!parentNode.children) {
          parentNode.children = [];
        }
        parentNode.children.push(newLocationNode);
        // Update tree data and UI
        this.dataSource.setData([...this.treeData]);
        this.populateFlatNodeMap(this.treeData);
        if (this.selectedNode) {
          this.treeControl.expand(this.selectedNode);
        }
        this.locationForm.reset();
        this.isLocationModal = false;
      }
      if (this.modalType === 'edit') {
        // Find the selected node in the tree
        if (!this.selectedNode) return;
        const currentNode = this.flatNodeMap.get(this.selectedNode);
        if (!currentNode) return;
        // Update the node properties
        currentNode.title = this.locationForm.value.display_name;
        currentNode.extraData = { ...currentNode.extraData, ...this.locationForm.value };
        currentNode.extraData.spoc_detail = this.spocDetail.getRawValue();
        // Update the tree data properly
        this.updateTreeData(this.treeData, currentNode.key, currentNode);
        // Ensure tree data refreshes correctly
        this.dataSource.setData([...this.treeData]);
        this.treeControl.expand(currentNode);
        // Make payload for backend
        this.makePayloadData(currentNode, 'edit');
        // Reset form and close modal
        this.locationForm.reset();
        this.isLocationModal = false;
      }
    }
  }
  updateTreeData(nodes: NzTreeNode[] | undefined, key: string, updatedNode: NzTreeNode) {
    if (!nodes) return;
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i] && nodes[i].key === key) {
        nodes[i] = { ...nodes[i], ...updatedNode };
        return;
      }
      if (nodes[i]?.children?.length) { // Safe navigation to avoid undefined errors
        this.updateTreeData(nodes[i].children, key, updatedNode);
      }
    }
  }
  makePayloadData(data: NzTreeNode, mode: 'add' | 'edit') {
    if (mode === 'add') {
      if (!data.parent) return;

      // Get parent group_id - for existing groups from API
      const parentGroupId = data.parent.extraData?.group_id;

      // Build parent structure
      let parentStructure: any = null;
      if (parentGroupId) {
        // Existing group with group_id
        parentStructure = { group_id: parentGroupId };
      } else {
        // Newly created group - use group_name with nested parent structure
        const buildParentStructure = (node: NzTreeNode): any => {
          if (!node) return null;
          if (node.extraData?.group_id) {
            return { group_id: node.extraData.group_id };
          }
          const parent: any = { group_name: node.title };
          if (node.parent) {
            const parentStruct = buildParentStructure(node.parent);
            if (parentStruct) {
              parent.parent = parentStruct;
            }
          }
          return parent;
        };
        parentStructure = buildParentStructure(data.parent);
      }

      const locationData: any = {
        registered_name: data.formData.registered_name,
        display_name: data.formData.display_name,
        registered_address: data.formData.registered_address,
        latitude: data.formData.latitude ?? undefined,
        longitude: data.formData.longitude ?? undefined,
        spoc_details: data.formData.spoc_detail,
        parent: parentStructure,
        is_deleted: false,
      };

      this.newLocationList.push(locationData);
      if (this.newLocationList.length > 0) this.isGroupDirty = true;
    }
    if (mode === 'edit') {
      const locationId = data.extraData?.location_id;
      if (!locationId) return;

      // Build updateData with only changed fields
      const updateData: any = {};

      // Check for changes in editable fields
      if (this.originalLocationData?.registered_name !== data.extraData.registered_name) {
        updateData.registered_name = data.extraData.registered_name;
      }
      if (this.originalLocationData?.display_name !== data.extraData.display_name) {
        updateData.display_name = data.extraData.display_name;
      }
      if (this.originalLocationData?.registered_address !== data.extraData.registered_address) {
        updateData.registered_address = data.extraData.registered_address;
      }
      if (this.originalLocationData?.latitude !== data.extraData.latitude) {
        updateData.latitude = data.extraData.latitude;
      }
      if (this.originalLocationData?.longitude !== data.extraData.longitude) {
        updateData.longitude = data.extraData.longitude;
      }

      // Always include spoc_details if changed
      const originalSpoc = JSON.stringify(this.originalLocationData?.spoc_detail || this.originalLocationData?.spoc_details || []);
      const currentSpoc = JSON.stringify(data.extraData.spoc_detail || data.extraData.spoc_details || []);
      if (originalSpoc !== currentSpoc) {
        updateData.spoc_details = data.extraData.spoc_detail || data.extraData.spoc_details;
      }

      // Check if there are any changes
      if (Object.keys(updateData).length > 0) {
        const updatePayload = {
          type: 'location',
          location_id: locationId, // Used for API URL param
          updateData: updateData
        };

        const existingIndex = this.updateList.findIndex(
          (item: any) => item.type === 'location' && item.location_id === locationId
        );
        if (existingIndex > -1) {
          this.updateList[existingIndex].updateData = { ...this.updateList[existingIndex].updateData, ...updateData };
        } else {
          this.updateList.push(updatePayload);
        }
        this.isGroupDirty = true;
      }
    }
  }
  private checkDuplicate(node: any, groupName: string): boolean {
    if (!node || !Array.isArray(node)) return false;
    for (let child of node) {
      if (child.title.trim().toLowerCase() === groupName.trim().toLowerCase()) {
        return true;
      }
      if (child.children && this.checkDuplicate(child.children, groupName)) {
        return true;
      }
    }
    return false;
  }
  deleteNode(node: FlatNode) {
    const treeNode = this.flatNodeMap.get(node);
    if (treeNode?.children && treeNode.children.length > 0) {
      this.commonSvc.message.error('Please remove all children first!');
      return;
    }
    if (!treeNode) return;

    // Track deletion for API - soft delete with is_deleted: true
    if (treeNode.type === 'location' && treeNode.extraData?.location_id) {
      const deletePayload = {
        type: 'location',
        location_id: treeNode.extraData.location_id,
        updateData: { is_deleted: true }
      };
      const existingIndex = this.updateList.findIndex(
        (item: any) => item.type === 'location' && item.location_id === treeNode.extraData.location_id
      );
      if (existingIndex > -1) {
        this.updateList[existingIndex].updateData = { is_deleted: true };
      } else {
        this.updateList.push(deletePayload);
      }
      this.isGroupDirty = true;
    } else if (treeNode.type === 'group' && treeNode.extraData?.group_id) {
      const deletePayload = {
        type: 'group',
        group_id: treeNode.extraData.group_id,
        updateData: { is_deleted: true }
      };
      const existingIndex = this.updateList.findIndex(
        (item: any) => item.type === 'group' && item.group_id === treeNode.extraData.group_id
      );
      if (existingIndex > -1) {
        this.updateList[existingIndex].updateData = { is_deleted: true };
      } else {
        this.updateList.push(deletePayload);
      }
      this.isGroupDirty = true;
    }

    // Remove from UI
    let parentTreeNode: NzTreeNode | null = null;
    for (const rootNode of this.treeData) {
      parentTreeNode = this.findParentNode(rootNode, treeNode);
      if (parentTreeNode) break;
    }
    if (parentTreeNode) {
      parentTreeNode.children = parentTreeNode.children?.filter(child => child.key !== treeNode.key) || [];
    } else {
      this.treeData = this.treeData.filter(root => root.key !== treeNode.key);
    }
    this.flatNodeMap.delete(node);
    this.nestedNodeMap.delete(treeNode);
    this.dataSource.setData([...this.treeData]);
    this.treeControl.expandAll();
  }
  private findParentNode(parent: NzTreeNode, targetNode: NzTreeNode): NzTreeNode | null {
    if (!parent.children) return null;
    for (const child of parent.children) {
      if (child.key === targetNode.key) {
        return parent;
      }
      const foundParent = this.findParentNode(child, targetNode);
      if (foundParent) return foundParent;
    }
    return null;
  }
  submit() {
    const customerId = Number(this.customer_id);
    let pendingRequests = 0;
    let completedRequests = 0;

    const checkAllComplete = () => {
      completedRequests++;
      if (completedRequests === pendingRequests) {
        this.isGroupDirty = false;
        this.getLocationTree();
      }
    };

    // Count pending requests
    if (this.newLocationList.length > 0) pendingRequests++;
    if (this.updateList.length > 0) pendingRequests++;

    if (pendingRequests === 0) return;

    // Add new locations
    if (this.newLocationList.length > 0) {
      const newLocationPayload = { customer_id: customerId, locationList: this.newLocationList };
      this.commonSvc.addLocation(newLocationPayload).subscribe((res: any) => {
        if (res) {
          this.commonSvc.message.success('Location added successfully!');
          this.newLocationList = [];
          checkAllComplete();
        }
      });
    }

    // Update existing locations and groups (single API per item)
    if (this.updateList.length > 0) {
      let updateCompleted = 0;
      const totalUpdates = this.updateList.length;

      this.updateList.forEach((item: any) => {
        const id = item.type === 'location' ? item.location_id : item.group_id;
        const updatePayload = {
          customer_id: customerId,
          type: item.type,
          updateData: item.updateData
        };

        this.commonSvc.updateLocation(id, updatePayload).subscribe((res: any) => {
          updateCompleted++;
          if (updateCompleted === totalUpdates) {
            this.commonSvc.message.success('Updated successfully!');
            this.updateList = [];
            checkAllComplete();
          }
        });
      });
    }
  }

  // Move functionality
  openMoveModal(node: FlatNode, type: 'location' | 'group') {
    this.selectedNode = node;
    this.moveType = type;
    this.selectedTargetGroupId = null;
    this.buildAvailableGroupsList(node, type);
    this.isMoveModal = true;
  }

  private buildAvailableGroupsList(excludeNode: FlatNode, type: 'location' | 'group') {
    this.availableGroups = [];
    const excludeKey = excludeNode.key;
    const treeNode = this.flatNodeMap.get(excludeNode) || this.findTreeNodeByKey(excludeNode.key);

    // For groups, add option to make it a root group
    if (type === 'group') {
      this.availableGroups.push({ id: null, name: '-- Make Root Group --', level: 0 });
    }

    // For locations, add option to remove from group
    if (type === 'location') {
      this.availableGroups.push({ id: null, name: '-- Remove from Group --', level: 0 });
    }

    // Recursively collect all groups except the current node and its descendants
    const collectGroups = (nodes: NzTreeNode[], level: number = 0) => {
      for (const node of nodes) {
        // Skip if this is the node being moved or if it's a location
        if (node.key === excludeKey || node.isLeaf) continue;

        // Skip if this node is a descendant of the node being moved (for groups)
        if (type === 'group' && this.isDescendant(treeNode, node)) continue;

        // Only add groups that have a group_id (existing groups from API)
        if (node.extraData?.group_id) {
          const indent = '—'.repeat(level);
          this.availableGroups.push({
            id: node.extraData.group_id,
            name: level > 0 ? `${indent} ${node.title}` : node.title,
            level: level
          });
        }

        // Recurse into children
        if (node.children && node.children.length > 0) {
          collectGroups(node.children, level + 1);
        }
      }
    };

    collectGroups(this.treeData);
  }

  private isDescendant(parent: NzTreeNode | null, potentialDescendant: NzTreeNode): boolean {
    if (!parent || !parent.children) return false;
    for (const child of parent.children) {
      if (child.key === potentialDescendant.key) return true;
      if (this.isDescendant(child, potentialDescendant)) return true;
    }
    return false;
  }

  confirmMove() {
    if (!this.selectedNode) {
      this.commonSvc.message.error('No node selected');
      return;
    }
    const treeNode = this.flatNodeMap.get(this.selectedNode) || this.findTreeNodeByKey(this.selectedNode.key);
    if (!treeNode) {
      this.commonSvc.message.error('Unable to find the selected item');
      return;
    }

    const id = this.moveType === 'location'
      ? treeNode.extraData?.location_id
      : treeNode.extraData?.group_id;

    if (!id) {
      this.commonSvc.message.error('Cannot move unsaved items. Please save first.');
      this.isMoveModal = false;
      return;
    }

    const updatePayload = {
      type: this.moveType,
      [this.moveType === 'location' ? 'location_id' : 'group_id']: id,
      updateData: this.moveType === 'location'
        ? { group_id: this.selectedTargetGroupId }
        : { parent_group_id: this.selectedTargetGroupId }
    };

    // Add to update list
    const existingIndex = this.updateList.findIndex(
      (item: any) => item.type === this.moveType &&
        (this.moveType === 'location' ? item.location_id === id : item.group_id === id)
    );

    if (existingIndex > -1) {
      this.updateList[existingIndex].updateData = {
        ...this.updateList[existingIndex].updateData,
        ...updatePayload.updateData
      };
    } else {
      this.updateList.push(updatePayload);
    }

    this.isGroupDirty = true;
    this.isMoveModal = false;
    this.commonSvc.message.success(`${this.moveType === 'location' ? 'Location' : 'Group'} will be moved on submit`);
  }
}